/**
 * Company Detail Page
 *
 * Displays company details with tabs for info, employees, notes, and default fees.
 */

import { format, parseISO } from 'date-fns';
import {
  ArrowLeft,
  Building2,
  DollarSign,
  Edit,
  Mail,
  MapPin,
  Phone,
  StickyNote,
  Trash2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CompanyDefaultFeesTab from '../components/CompanyDefaultFeesTab';
import CompanyEmployeesTab from '../components/CompanyEmployeesTab';
import CompanyNotesTab from '../components/CompanyNotesTab';
import { useCompany, useDeleteCompany } from '../hooks';
import type { CompanyGroupName, CompanyStatus } from '../types';

function getStatusBadgeVariant(status: CompanyStatus): 'default' | 'secondary' {
  return status === 'active' ? 'default' : 'secondary';
}

function getGroupBadgeClass(groupName: CompanyGroupName): string {
  const classes: Record<CompanyGroupName, string> = {
    Closing: 'bg-blue-100 text-blue-800',
    Payoffs: 'bg-orange-100 text-orange-800',
    Services: 'bg-green-100 text-green-800',
    Government: 'bg-purple-100 text-purple-800',
    Other: 'bg-gray-100 text-gray-800',
  };
  return classes[groupName] || 'bg-gray-100 text-gray-800';
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch {
    return '-';
  }
}

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('info');

  const { data: company, isLoading, error } = useCompany(id || '');
  const deleteCompany = useDeleteCompany();

  const handleBack = () => {
    navigate('/contacts');
  };

  const handleEdit = () => {
    navigate(`/contacts/${id}/edit`);
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to delete this company?')) return;

    try {
      await deleteCompany.mutateAsync(id);
      navigate('/contacts');
    } catch (err) {
      console.error('Failed to delete company:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Loading company...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-600">Error loading company: {error.message}</div>
        <Button variant="outline" onClick={handleBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Companies
        </Button>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-6">
        <div className="text-gray-500">Company not found</div>
        <Button variant="outline" onClick={handleBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Companies
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className={`flex ${company.contact_type === 'individual' && 'items-center'} gap-4`}>
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-gray-900">
                {company.contact_type === 'individual'
                  ? company.primary_contact_name || company.name
                  : company.name}
              </h1>
              <Badge variant={getStatusBadgeVariant(company.status)}>
                {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
              </Badge>
            </div>
            {company.category && company.contact_type === 'organization' && (
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getGroupBadgeClass(company.category.group_name)}`}
                >
                  {company.category.group_name}
                </span>
                <span className="text-sm text-gray-500">{company.category.name}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="outline"
            onClick={handleDelete}
            disabled={deleteCompany.isPending}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="info" className="gap-2">
            <Building2 className="h-4 w-4" />
            Info
          </TabsTrigger>
          {company.contact_type === 'organization' && (
            <TabsTrigger value="employees" className="gap-2">
              <Users className="h-4 w-4" />
              Employees
            </TabsTrigger>
          )}
          <TabsTrigger value="notes" className="gap-2">
            <StickyNote className="h-4 w-4" />
            Notes
          </TabsTrigger>
          <TabsTrigger value="fees" className="gap-2">
            <DollarSign className="h-4 w-4" />
            Default Fees
          </TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {company.contact_type === 'organization' && company.primary_contact_name && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Primary Contact</div>
                    <div>{company.primary_contact_name}</div>
                    {company.primary_contact_title && (
                      <div className="text-sm text-gray-500">{company.primary_contact_title}</div>
                    )}
                  </div>
                )}

                {company.contact_type === 'individual' && company.primary_contact_title && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Title</div>
                    <div>{company.primary_contact_title}</div>
                  </div>
                )}

                {(company.email || company.primary_contact_email) && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <a
                      href={`mailto:${company.contact_type === 'individual' ? company.primary_contact_email || company.email : company.email}`}
                      className="text-blue-600 hover:underline"
                    >
                      {company.contact_type === 'individual'
                        ? company.primary_contact_email || company.email
                        : company.email}
                    </a>
                  </div>
                )}

                {company.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <a href={`tel:${company.phone}`} className="text-blue-600 hover:underline">
                      {company.phone}
                    </a>
                  </div>
                )}

                {company.fax && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{company.fax}</span>
                    <span className="text-xs text-gray-400">(Fax)</span>
                  </div>
                )}

                {company.website && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Website</div>
                    <a
                      href={
                        company.website.startsWith('http')
                          ? company.website
                          : `https://${company.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {company.website}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Address */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Address</CardTitle>
              </CardHeader>
              <CardContent>
                {company.address_street ||
                company.address_city ||
                company.address_state ||
                company.address_zip ? (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-gray-400 mt-1" />
                    <div>
                      {company.address_street && <div>{company.address_street}</div>}
                      {company.address_unit && <div>{company.address_unit}</div>}
                      <div>
                        {[company.address_city, company.address_state, company.address_zip]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                      {company.address_country && company.address_country !== 'USA' && (
                        <div>{company.address_country}</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-400">No address provided</div>
                )}
              </CardContent>
            </Card>

            {/* License Information */}
            {(company.license_number || company.nmls_id) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">License Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {company.license_number && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">License Number</div>
                      <div>
                        {company.license_number}
                        {company.license_state && ` (${company.license_state})`}
                      </div>
                    </div>
                  )}
                  {company.license_expiry && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">License Expiry</div>
                      <div>{formatDate(company.license_expiry)}</div>
                    </div>
                  )}
                  {company.nmls_id && (
                    <div>
                      <div className="text-sm font-medium text-gray-500">NMLS ID</div>
                      <div>{company.nmls_id}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Tags & Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Additional Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {company.tags && company.tags.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-2">Tags</div>
                    <div className="flex flex-wrap gap-1">
                      {company.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {company.internal_notes && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-1">Internal Notes</div>
                    <div className="text-sm text-gray-600 whitespace-pre-wrap">
                      {company.internal_notes}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-sm font-medium text-gray-500">Usage</div>
                  <div className="text-sm text-gray-600">
                    Used {company.usage_count} times
                    {company.last_used_at && ` (last: ${formatDate(company.last_used_at)})`}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Employees Tab - Only for Organizations */}
        {company.contact_type === 'organization' && (
          <TabsContent value="employees">
            <CompanyEmployeesTab companyId={company.id} />
          </TabsContent>
        )}

        {/* Notes Tab */}
        <TabsContent value="notes">
          <CompanyNotesTab companyId={company.id} />
        </TabsContent>

        {/* Default Fees Tab */}
        <TabsContent value="fees">
          <CompanyDefaultFeesTab companyId={company.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
